import type { SelfServiceSubmissionRow } from '../../types/selfService';

// Shared submission-review predicates. Consumed by both the Submissions page
// (Pending Review tab composition) and the Publisher Overview (Needs your
// review section) so the two surfaces cannot drift on what counts as
// "publisher action needed". See CONTEXT.md → "Overview submission review
// predicate" and CTV-530.
//
// Accepts a Pick of the SelfServiceSubmissionRow so each page can supply its
// own narrower row shape without a widening cast.
export type SubmissionReviewCandidate = Pick<
  SelfServiceSubmissionRow,
  'status' | 'creatives'
>;

// CTV-440: a submission with a pending_review creative needs the publisher's
// attention even though its top-level status is 'approved'.
// CTV-611: the edit-level `pendingCreativeSwap` field is retired; any
// pending_review creative on an approved row is what marks a swap awaiting
// per-creative review.
export const isSwapPending = (s: SubmissionReviewCandidate): boolean =>
  s.status === 'approved' && (s.creatives ?? []).some((c) => c.status === 'pending_review');

// CTV-466: a rejected submission is waiting on the advertiser to resubmit or
// archive. The publisher's Submissions Pending Review tab surfaces these for
// pipeline visibility, but the Overview's "Needs your review" section
// deliberately excludes them — the publisher has no action to take.
export const isAwaitingAdvertiserFix = (s: SubmissionReviewCandidate): boolean =>
  s.status === 'creative_rejected';

// Composed predicate for the Overview's "Needs your review" section.
//
// ADR CTV-732 narrowed this: `needsLibraryUpload` is gone. A creative
// missing from Publica's library is no longer publisher-recoverable work — it
// surfaces on the super-admin worklist (/admin/publica-uploads) as failed
// verification. What remains is genuine content review, plus
// `isAwaitingAdvertiserFix` which is deliberately excluded (advertiser action,
// not publisher action).
export const needsPublisherReview = (s: SubmissionReviewCandidate): boolean =>
  s.status === 'payment_authorized' || isSwapPending(s);
